import { useEffect, useState } from "react";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import { findAllCustomers, deleteCustomer } from "../../services/customer";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Spinner } from "../../components/spinner";
import { Snackbar } from "../../components/snackbar";
import { DEFAULT_SNACK } from "../../utils/snacks";

const ITEMS_PER_PAGE = 10;

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (searchTerm = "") => {
    try {
      setLoading(true);
      const data = await findAllCustomers({ search: searchTerm });
      setCustomers(Array.isArray(data) ? data : []);
      setCurrentPage(1); // Reset a página 1 al buscar
    } catch (error) {
      console.error("Error fetching customers:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al cargar clientes",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  const handleDelete = async (customerId, customerName) => {
    if (
      window.confirm(
        `¿Estás seguro de eliminar al cliente ${customerName}?\n\nNota: Si tiene vehículos asociados, solo se deshabilitará.`
      )
    ) {
      try {
        const result = await deleteCustomer(customerId);

        if (result.success) {
          setSnackbar({
            visible: true,
            severity: "success",
            message: result.message || "Cliente eliminado exitosamente",
          });
          setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
          fetchCustomers(search);
        } else {
          setSnackbar({
            visible: true,
            severity: "warning",
            message: result.message || "No se pudo eliminar el cliente",
          });
          setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
        }
      } catch (error) {
        console.error("Error deleting customer:", error);
        setSnackbar({
          visible: true,
          severity: "error",
          message: "Error al eliminar cliente",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      }
    }
  };

  const handleEdit = (customerId) => {
    navigate(`/dashboard/customers/edit?${customerId}`);
  };

  const handleAddNew = () => {
    navigate("/dashboard/customers/new");
  };

  const filteredCustomers = customers.filter((customer) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.customer_id?.toLowerCase().includes(searchLower) ||
      customer.phone?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  // Paginación
  const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center p-4 bg-sidebar-bg">
          <Wrapper>
            <Spinner />
          </Wrapper>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4">
        <Wrapper>
          <div className="flex-1 p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                      <PeopleIcon className="text-white" fontSize="small" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">Clientes</h1>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Gestiona todos los clientes del taller • Página {currentPage} de{" "}
                    {totalPages}
                  </p>
                </div>
                <button
                  onClick={handleAddNew}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                >
                  <PersonAddIcon fontSize="small" />
                  Nuevo Cliente
                </button>
              </div>

              <div className="mb-6">
                <form onSubmit={handleSearch} className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <SearchIcon className="text-gray-500" sx={{ fontSize: 20 }} />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, RUT, teléfono o email..."
                    className="w-full pl-12 pr-4 py-3 bg-sidebar-bg border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </form>
              </div>

              <div className="bg-sidebar-bg rounded-xl shadow-sm border border-sidebar-accent overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-sidebar-accent bg-sidebar-bg">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <BadgeIcon fontSize="small" className="text-gray-400" />
                            Nombre
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          RUT
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <PhoneIcon fontSize="small" className="text-gray-400" />
                            Teléfono
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <EmailIcon fontSize="small" className="text-gray-400" />
                            Email
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sidebar-accent bg-black">
                      {currentCustomers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <PeopleIcon className="mx-auto text-gray-300 mb-3" fontSize="large" />
                            <p className="text-gray-500 text-sm">
                              {search
                                ? "No se encontraron clientes"
                                : "No hay clientes registrados"}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        currentCustomers.map((customer) => (
                          <tr
                            key={customer._id}
                            className="hover:bg-sidebar-accent transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-white capitalize">
                                {customer.name}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200">
                                {customer.customer_id}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200">{customer.phone}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200">
                                {customer.email || "-"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEdit(customer._id)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                  title="Editar cliente"
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                                <button
                                  onClick={() => handleDelete(customer._id, customer.name)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                                  title="Eliminar cliente"
                                >
                                  <DeleteIcon fontSize="small" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredCustomers.length > 0 && (
                  <>
                    <div className="px-6 py-4 border-t border-sidebar-accent bg-sidebar-bg flex items-center justify-between">
                      <p className="text-sm text-gray-300">
                        Mostrando{" "}
                        <span className="font-semibold text-white">
                          {startIndex + 1}
                        </span>
                        -
                        <span className="font-semibold text-white">
                          {Math.min(endIndex, filteredCustomers.length)}
                        </span>{" "}
                        de{" "}
                        <span className="font-semibold text-white">
                          {filteredCustomers.length}
                        </span>{" "}
                        {filteredCustomers.length === 1 ? "cliente" : "clientes"}
                      </p>

                      {totalPages > 1 && (
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Wrapper>
      </div>

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </button>

      <div className="flex gap-1">
        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-3 py-2 text-gray-500 text-sm"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[36px] px-3 py-2 text-sm rounded-lg font-medium transition-all ${
                currentPage === page
                  ? "bg-blue-600 text-white"
                  : "bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white"
              }`}
            >
              {page}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </button>
    </div>
  );
};
